import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  SearchPersonsDocument,
  StringFilterCaseSensitivity,
  type SearchPersonsQuery,
  type SearchPersonsQueryVariables,
} from "@/graphql/generated/operations";
import { Avatar, type PersonInfo } from "@/components/common/Avatar";
import { useStartChat } from "./use-start-chat";

type SearchPerson = SearchPersonsQuery["persons"]["nodes"][number];
type SearchInput = SearchPersonsQueryVariables["input"];

const PAGE_SIZE = 20;
const MIN_CHARS = 2;

// People search to start a new conversation. Mirrors discovery's
// PersonPickerComponent + GridLazyDataView pattern: cursor-based infinite
// scroll, observe a sentinel at the bottom and fetch the next page on scroll.
// We drive the query through `client.query({ fetchPolicy: "no-cache" })` and
// accumulate pages locally so this paged search doesn't pollute the global
// PersonConnection cache used by the topbar SearchBar and elsewhere.
export function NewChatSearch({ onPicked }: { onPicked: () => void }) {
  const intl = useIntl();
  const client = useApolloClient();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const term = debounced.trim();
  const skip = term.length < MIN_CHARS;

  const [persons, setPersons] = useState<SearchPerson[]>([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);

  // Cancellation token — each new search term invalidates in-flight responses.
  // Without this, a slow "ja" response could land after a fast "jane" response
  // and overwrite the correct list with stale data.
  const requestSeq = useRef(0);

  const runQuery = useCallback(
    async (after: string | null) => {
      const seq = ++requestSeq.current;
      const input: SearchInput = {
        first: PAGE_SIZE,
        where: {
          formattedName: {
            contains: term,
            caseSensitivity: StringFilterCaseSensitivity.CaseInsensitive,
          },
        },
        ...(after ? { after } : {}),
      };
      const result = await client.query({
        query: SearchPersonsDocument,
        variables: { input },
        fetchPolicy: "no-cache",
      });
      if (seq !== requestSeq.current) return null; // a newer search superseded us
      return result.data;
    },
    [client, term],
  );

  // First page (and reset) whenever the search term changes.
  useEffect(() => {
    if (skip) {
      setPersons([]);
      setEndCursor(null);
      setHasNextPage(false);
      setTotalCount(0);
      requestSeq.current++; // invalidate any in-flight request
      return;
    }
    let cancelled = false;
    setLoading(true);
    void runQuery(null).then((data) => {
      if (cancelled || !data) return;
      const nodes = (data.persons?.nodes ?? []) as SearchPerson[];
      setPersons(nodes);
      setEndCursor(data.persons?.pageInfo?.endCursor ?? null);
      setHasNextPage(Boolean(data.persons?.pageInfo?.hasNextPage));
      setTotalCount(data.persons?.totalCount ?? 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [skip, runQuery]);

  const loadMore = useCallback(async () => {
    if (fetchingMore || loading || !hasNextPage || !endCursor) return;
    setFetchingMore(true);
    try {
      const data = await runQuery(endCursor);
      if (!data) return;
      const nodes = (data.persons?.nodes ?? []) as SearchPerson[];
      // Dedupe by id — the cursor should prevent overlap, but a server-side
      // change between pages could theoretically repeat an entry.
      setPersons((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...nodes.filter((n) => !seen.has(n.id))];
      });
      setEndCursor(data.persons?.pageInfo?.endCursor ?? null);
      setHasNextPage(Boolean(data.persons?.pageInfo?.hasNextPage));
    } finally {
      setFetchingMore(false);
    }
  }, [endCursor, fetchingMore, hasNextPage, loading, runQuery]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || skip) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      { rootMargin: "100px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, skip, persons.length]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="p-3">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={intl.formatMessage({
            id: "chat.searchPeople",
            defaultMessage: "Search people…",
          })}
          autoFocus
          className="w-full rounded bg-surface px-3 py-1.5 text-sm outline-none ring-1 ring-ink/15 focus:ring-accent"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {skip ? (
          <p className="px-4 py-3 text-xs text-ink/45">
            <FormattedMessage
              id="chat.searchHint"
              defaultMessage="Type at least {n} characters"
              values={{ n: MIN_CHARS }}
            />
          </p>
        ) : loading ? (
          <p className="px-4 py-3 text-sm text-ink/45">
            <FormattedMessage id="common.loading" defaultMessage="Loading…" />
          </p>
        ) : persons.length === 0 ? (
          <p className="px-4 py-3 text-sm text-ink/45">
            <FormattedMessage id="common.noResults" defaultMessage="No results found" />
          </p>
        ) : (
          <>
            <ul>
              {persons.map((p) => (
                <PersonRow key={p.id} person={p} onPicked={onPicked} />
              ))}
            </ul>
            <div ref={sentinelRef} className="h-px" aria-hidden="true" />
            {fetchingMore ? (
              <p className="px-4 py-3 text-center text-xs text-ink/45">
                <FormattedMessage id="common.loading" defaultMessage="Loading…" />
              </p>
            ) : !hasNextPage && totalCount > PAGE_SIZE ? (
              <p className="px-4 py-3 text-center text-[11px] text-ink/40">
                <FormattedMessage
                  id="chat.searchEnd"
                  defaultMessage="{count} results"
                  values={{ count: totalCount }}
                />
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function PersonRow({ person, onPicked }: { person: SearchPerson; onPicked: () => void }) {
  const { busy, startChat } = useStartChat(person.id);
  const info: PersonInfo = {
    name: person.formattedName ?? "–",
    avatarContent: person.avatar?.content,
    avatarMime: person.avatar?.mimeType,
  };
  const sub = person.profession || person.city || person.country?.name || null;

  async function pick() {
    if (busy) return;
    await startChat();
    onPicked();
  }

  return (
    <li>
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-page/40 disabled:opacity-50"
      >
        <Avatar person={info} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{info.name}</p>
          {sub ? <p className="truncate text-xs text-ink/55">{sub}</p> : null}
        </div>
      </button>
    </li>
  );
}
